import { Table, Typography } from 'antd'
import CantileverCard from 'components/CantileverCard'
import { useRolodexEntries } from 'features/rolodex'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import {
  InspectionAssessment,
  InspectionAssessmentData,
  RolodexEntryType,
} from 'types'

const { Text } = Typography

const StyledTable = styled(Table<InspectionAssessmentData>)`
  .ant-table {
    border-radius: 8px 8px 8px 8px;
    overflow: hidden;
    background-color: transparent;
  }

  .ant-table-thead > tr > th {
    background-color: transparent;
    color: ${({ theme }) => theme.forecast.text1};
    font-weight: 500;
  }

  .ant-table-tbody > tr > td {
    color: ${({ theme }) => theme.forecast.text1};
  }

  .ant-table-tbody > tr:hover > td {
    background-color: ${({ theme }) => theme.forecast.background2} !important;
  }

  .ant-table-tbody > tr > td.ant-table-cell {
    border-bottom: 1px solid ${({ theme }) => theme.forecast.background2};
  }

  .ant-table-thead > tr > th.ant-table-cell {
    border-bottom: 1px solid ${({ theme }) => theme.forecast.background2};
  }

  .ant-table-placeholder {
    .ant-table-tbody > tr.ant-table-placeholder:hover > td {
      background-color: transparent !important;
    }
  }
`

interface AssessmentTableProps {
  assessments: InspectionAssessment[]
}

export const WalkdownInspectionAssessmentTable: React.FC<
  AssessmentTableProps
> = ({ assessments }) => {
  const unitIds = useMemo(
    () => assessments.map((assessment) => assessment.tags?.unit || ''),
    [assessments]
  )

  const { units } = useRolodexEntries({
    type: [RolodexEntryType.UNIT],
    ids: unitIds,
  })

  const unitsMap = useMemo(() => {
    const map = new Map()
    units.forEach((unit) => {
      map.set(unit.id, unit)
    })
    return map
  }, [units])

  const tableData = useMemo(() => {
    return assessments.map((assessment) => {
      const unit = unitsMap.get(assessment.tags?.unit || '')
      return {
        key: assessment.id,
        inspectionType: assessment.data.inspectionType || 'N/A',
        unitName: unit?.data?.unit_name || 'Unknown Unit',
        unitId: unit?.data?.unit_id || 'N/A',
        assessmentType: assessment.data.assessment_type || 'N/A',
        overallCondition: assessment.data.overall_condition || 'N/A',
        recommendedAction: assessment.data.recommended_action || 'N/A',
        notes: assessment.data.notes || '',
        createdAt: assessment.created_at,
      }
    })
  }, [assessments, unitsMap])

  const columns = [
    {
      title: 'Unit Name',
      dataIndex: 'unitName',
      key: 'unitName',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Unit ID',
      dataIndex: 'unitId',
      key: 'unitId',
    },
    {
      title: 'Assessment Type',
      dataIndex: 'assessmentType',
      key: 'assessmentType',
    },
    {
      title: 'Overall Condition',
      dataIndex: 'overallCondition',
      key: 'overallCondition',
      render: (condition: string) => {
        let color = 'default'
        if (condition === 'Good') color = 'green'
        else if (condition === 'Fair') color = 'orange'
        else if (condition === 'Poor') color = 'red'

        return <Text style={{ color }}>{condition}</Text>
      },
    },
    {
      title: 'Recommended Action',
      dataIndex: 'recommendedAction',
      key: 'recommendedAction',
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (text: string) => (
        <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 200 }}>
          {text}
        </Text>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ]

  if (assessments.length === 0) {
    return (
      <CantileverCard>
        <CantileverCard.CardHeader leadingContent='Inspection Assessments' />
        <CantileverCard.Section topPadding>
          <CantileverCard.Group>
            <Text type='secondary'>No assessments found</Text>
          </CantileverCard.Group>
        </CantileverCard.Section>
      </CantileverCard>
    )
  }

  return (
    <CantileverCard>
      <CantileverCard.CardHeader leadingContent='Inspection Assessments' />
      <CantileverCard.Section topPadding>
        <CantileverCard.Group>
          <StyledTable
            dataSource={tableData}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </CantileverCard.Group>
      </CantileverCard.Section>
    </CantileverCard>
  )
}
